const React = require('react');
const { createElement: h, useState, useEffect } = React;
const oauthApps = require('../lib/oauthApps');
require('./OAuthConsentPage.css');

// ---------------------------------------------------------------------------
// Consent screen at /oauth/authorize.
//
// A third-party app sends the user here. We describe who is asking and exactly
// what they want, then the user approves or denies. Approving mints a one-time
// code and redirects back to the app.
//
// The signed-out case is handled by App.js, which sends the user to /login and
// returns them here afterwards — so by the time this renders there IS a session.
// ---------------------------------------------------------------------------

// The OAuth params arrive in the URL. We hand the whole query string back to
// the API rather than re-encoding field by field.
function currentQuery() {
  return window.location.search || '';
}

function readParams() {
  var params = new URLSearchParams(window.location.search || '');
  return {
    client_id: params.get('client_id') || '',
    redirect_uri: params.get('redirect_uri') || '',
    response_type: params.get('response_type') || 'code',
    scope: params.get('scope') || '',
    state: params.get('state') || '',
    code_challenge: params.get('code_challenge') || '',
    code_challenge_method: params.get('code_challenge_method') || ''
  };
}

// Denying must tell the app what happened, per RFC 6749, instead of leaving
// the user on a dead end.
function buildDenyUrl(redirectUri, state) {
  try {
    var url = new URL(redirectUri);
    url.searchParams.set('error', 'access_denied');
    url.searchParams.set('error_description', 'The user denied the request.');
    if (state) url.searchParams.set('state', state);
    return url.toString();
  } catch (e) {
    return '';
  }
}

function OAuthConsentPage(props) {
  var user = props && props.user ? props.user : null;
  var [info, setInfo] = useState(null);
  var [loading, setLoading] = useState(true);
  var [error, setError] = useState('');
  var [submitting, setSubmitting] = useState(false);

  useEffect(function() {
    oauthApps.getAuthorizeInfo(currentQuery())
      .then(function(data) { setInfo(data); })
      .catch(function(err) {
        setError((err && err.message) || 'This authorization request is not valid.');
      })
      .finally(function() { setLoading(false); });
  }, []);

  function handleApprove() {
    if (submitting) return;
    setSubmitting(true);
    setError('');
    oauthApps.approveAuthorization(readParams())
      .then(function(result) {
        if (result && result.redirectTo) {
          window.location.href = result.redirectTo;
          return;
        }
        setError('Could not complete the authorization.');
        setSubmitting(false);
      })
      .catch(function(err) {
        setError((err && err.message) || 'Could not complete the authorization.');
        setSubmitting(false);
      });
  }

  function handleDeny() {
    var target = buildDenyUrl(info && info.redirectUri, info && info.state);
    if (target) {
      window.location.href = target;
      return;
    }
    window.location.href = '/dashboard';
  }

  if (loading) {
    return h('div', { className: 'oac-root' },
      h('div', { className: 'oac-card' },
        h('p', { className: 'oac-loading' }, 'Checking this request...')
      )
    );
  }

  // A bad client_id or redirect_uri must never bounce the user onward — show
  // the problem here instead.
  if (error && !info) {
    return h('div', { className: 'oac-root' },
      h('div', { className: 'oac-card' },
        h('div', { className: 'oac-brand' }, 'Alleflow'),
        h('h1', { className: 'oac-title' }, 'This request is not valid'),
        h('p', { className: 'oac-error' }, error),
        h('p', { className: 'oac-fineprint' },
          'Nothing was shared. You can safely close this page and contact the app that sent you here.')
      )
    );
  }

  var appName = (info && info.client && info.client.name) || 'An application';
  var scopeDetails = (info && info.scopeDetails) || [];

  return h('div', { className: 'oac-root' },
    h('div', { className: 'oac-card' },
      h('div', { className: 'oac-brand' }, 'Alleflow'),

      h('div', { className: 'oac-app' },
        info && info.client && info.client.logoUrl
          ? h('img', {
              className: 'oac-app__logo',
              src: info.client.logoUrl,
              alt: '',
              onError: function(e) { e.target.style.display = 'none'; }
            })
          : h('div', { className: 'oac-app__logo oac-app__logo--fallback' },
              appName.charAt(0).toUpperCase()),
        h('div', { className: 'oac-app__meta' },
          h('h1', { className: 'oac-title' }, appName + ' wants to access your Alleflow account'),
          info && info.client && info.client.description
            ? h('p', { className: 'oac-app__desc' }, info.client.description)
            : null
        )
      ),

      user && user.username
        ? h('p', { className: 'oac-signed-in' },
            'Signed in as ', h('strong', null, user.username))
        : null,

      h('div', { className: 'oac-scopes' },
        h('p', { className: 'oac-scopes__title' }, 'This will allow ' + appName + ' to:'),
        scopeDetails.map(function(item) {
          return h('div', { className: 'oac-scope', key: item.scope },
            h('span', { className: 'oac-scope__check' }, '✓'),
            h('span', { className: 'oac-scope__text' },
              h('span', { className: 'oac-scope__label' }, item.label),
              h('span', { className: 'oac-scope__desc' }, item.description)
            )
          );
        })
      ),

      error ? h('p', { className: 'oac-error' }, error) : null,

      h('div', { className: 'oac-actions' },
        h('button', {
          type: 'button',
          className: 'oac-deny',
          onClick: handleDeny,
          disabled: submitting
        }, 'Cancel'),
        h('button', {
          type: 'button',
          className: 'oac-approve',
          onClick: handleApprove,
          disabled: submitting
        }, submitting ? 'Authorizing...' : 'Allow access')
      ),

      info && info.redirectUri
        ? h('p', { className: 'oac-fineprint' },
            'You will be redirected to ',
            h('code', { className: 'oac-redirect' }, info.redirectUri))
        : null,

      h('p', { className: 'oac-fineprint' },
        'Only approve apps you trust. Access lasts until the app is removed by its developer.')
    )
  );
}

module.exports = OAuthConsentPage;
